import { create } from 'zustand';
import type { Key } from '../i18n/strings';

/*
    Shortcuts are stored as physical key codes (KeyJ, KeyD), never as characters.
    On a Turkish Q layout `#` is AltGr+3 and the key at the US `.` position types
    `ç`, so a character-based binding is either a chord or a surprise. Matching by
    position keeps every binding one keystroke on any QWERTY-derived layout.
*/

export type ActionId =
    | 'next'
    | 'prev'
    | 'compose'
    | 'reply'
    | 'replyAll'
    | 'forward'
    | 'archive'
    | 'move'
    | 'delete'
    | 'star'
    | 'unread'
    | 'refresh';

export const ACTIONS: { id: ActionId; label: Key; scope: Key }[] = [
    { id: 'next', label: 'keys.action.next', scope: 'keys.scope.list' },
    { id: 'prev', label: 'keys.action.prev', scope: 'keys.scope.list' },
    { id: 'compose', label: 'keys.action.compose', scope: 'keys.scope.global' },
    { id: 'reply', label: 'keys.action.reply', scope: 'keys.scope.reading' },
    { id: 'replyAll', label: 'keys.action.replyAll', scope: 'keys.scope.reading' },
    { id: 'forward', label: 'keys.action.forward', scope: 'keys.scope.reading' },
    { id: 'archive', label: 'keys.action.archive', scope: 'keys.scope.list' },
    { id: 'move', label: 'keys.action.move', scope: 'keys.scope.list' },
    { id: 'delete', label: 'keys.action.delete', scope: 'keys.scope.list' },
    { id: 'star', label: 'keys.action.star', scope: 'keys.scope.list' },
    { id: 'unread', label: 'keys.action.unread', scope: 'keys.scope.list' },
    { id: 'refresh', label: 'keys.action.refresh', scope: 'keys.scope.global' },
];

/*
    The shortcuts that are not the person's to change.

    Everything in ACTIONS is a bare letter and only fires outside a text field, which is what makes
    it remappable without colliding with typing. These four are the opposite: they carry a modifier
    and have to work with the cursor in a message, so they are fixed — and they are fixed at the
    combinations every mail client already agrees on, which is the whole reason a person tries them.

    What is deliberately NOT here: ctrl+r, ctrl+f, ctrl+s, ctrl+p. Outlook and Thunderbird use them
    on the desktop, where the browser has no claim. In a browser they are reload, find, save and
    print, and a mail client that swallows them has taken something from the person rather than
    given them anything.
*/
export const FIXED: { keys: string; label: Key; scope: Key }[] = [
    { keys: 'Ctrl/⌘+↵', label: 'keys.action.send', scope: 'keys.scope.compose' },
    { keys: 'Alt+S', label: 'keys.action.send', scope: 'keys.scope.compose' },
    { keys: 'Ctrl/⌘+A', label: 'keys.action.selectAll', scope: 'keys.scope.list' },
    { keys: 'Ctrl/⌘+K', label: 'keys.action.search', scope: 'keys.scope.global' },
];

export type PresetName = 'thunderbird' | 'gmail' | 'vim' | 'custom';

type Map = Record<ActionId, string>;

export const PRESETS: Record<Exclude<PresetName, 'custom'>, Map> = {
    thunderbird: {
        next: 'KeyF', prev: 'KeyB', compose: 'KeyN', reply: 'KeyR', replyAll: 'KeyL',
        forward: 'KeyW', archive: 'KeyA', move: 'KeyV', delete: 'KeyD', star: 'KeyS', unread: 'KeyM', refresh: 'KeyG',
    },
    gmail: {
        next: 'KeyJ', prev: 'KeyK', compose: 'KeyC', reply: 'KeyR', replyAll: 'KeyA',
        forward: 'KeyF', archive: 'KeyE', move: 'KeyV', delete: 'KeyD', star: 'KeyS', unread: 'KeyU', refresh: 'KeyG',
    },
    vim: {
        next: 'KeyJ', prev: 'KeyK', compose: 'KeyO', reply: 'KeyR', replyAll: 'KeyA',
        forward: 'KeyW', archive: 'KeyX', move: 'KeyV', delete: 'KeyD', star: 'KeyM', unread: 'KeyU', refresh: 'KeyG',
    },
};

/**
 * Resolves a preset plus overrides into a binding table.
 *
 * Exported so components can memoise it on (preset, overrides). Selecting
 * `s.map()` straight out of the store hands zustand a brand-new object on every
 * comparison, which it reads as a change and re-renders forever.
 */
export function keymap(preset: PresetName, overrides: Partial<Map>): Map {
    return { ...(preset === 'custom' ? PRESETS.gmail : PRESETS[preset]), ...overrides };
}

/** "KeyJ" → "J", "Digit3" → "3", "Period" → "." — what the user should see. */
export function keyLabel(code: string): string {
    if (!code) return '—';
    if (code.startsWith('Key')) return code.slice(3);
    if (code.startsWith('Digit')) return code.slice(5);
    const named: Record<string, string> = {
        Period: '.', Comma: ',', Slash: '/', Semicolon: ';', Quote: "'",
        BracketLeft: '[', BracketRight: ']', Backslash: '\\', Minus: '-', Equal: '=',
        Space: 'space', Enter: '⏎', Backspace: '⌫', Delete: 'del', Escape: 'esc',
    };
    return named[code] ?? code;
}

const KEY = 'webmail.keys.v1';

interface KeysState {
    preset: PresetName;
    overrides: Partial<Map>;
    map: () => Map;
    conflicts: () => Record<string, ActionId[]>;
    setPreset: (p: PresetName) => void;
    bind: (action: ActionId, code: string) => void;
    reset: () => void;
    /** Which action a physical key triggers, or null. */
    lookup: (code: string) => ActionId | null;
}

function load(): { preset: PresetName; overrides: Partial<Map> } {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return { preset: 'gmail', overrides: {} };
        const p = JSON.parse(raw) as { preset?: PresetName; overrides?: Partial<Map> };
        return { preset: p.preset ?? 'gmail', overrides: p.overrides ?? {} };
    } catch {
        return { preset: 'gmail', overrides: {} };
    }
}

const initial = load();

export const useKeys = create<KeysState>((set, get) => ({
    preset: initial.preset,
    overrides: initial.overrides,

    map: () => {
        const { preset, overrides } = get();
        return keymap(preset, overrides);
    },

    conflicts: () => {
        const map = get().map();
        const byCode: Record<string, ActionId[]> = {};
        for (const { id } of ACTIONS) {
            const code = map[id];
            if (!code) continue;
            (byCode[code] ??= []).push(id);
        }
        return Object.fromEntries(Object.entries(byCode).filter(([, list]) => list.length > 1));
    },

    setPreset: (preset) => {
        const next = { preset, overrides: preset === 'custom' ? get().overrides : {} };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(next);
    },

    bind: (action, code) => {
        const overrides = { ...get().overrides, [action]: code };
        const next = { preset: 'custom' as PresetName, overrides };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(next);
    },

    reset: () => {
        const next = { preset: 'gmail' as PresetName, overrides: {} };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(next);
    },

    lookup: (code) => {
        const map = get().map();
        for (const { id } of ACTIONS) if (map[id] === code) return id;
        return null;
    },
}));
